import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  useCanCreateNotes,
  useCanReadNotes,
  useNoteCategories,
} from '@dispatch/shared/hooks';
import {
  createOrReplaceNote,
  NoteCategoryDto,
  TaskNoteDto,
} from '@dispatch/shared/services';
import { ConfirmModal } from '../common/ConfirmModal';
import { DEFAULT_NOTE_CATEGORIES } from './default-note-categories';

export type AddNoteModalMode = 'create' | 'view' | 'edit';

export interface AddNoteModalProps {
  taskId: string;
  tenantId?: string;
  open: boolean;
  /**
   * 'create' (default): empty form, fresh noteId, Save creates.
   * 'view': read-only render of `note`; if the user has manage:notes,
   *   an Edit button transitions the modal into 'edit' mode.
   * 'edit': editable form pre-populated from `note`; Save calls PUT with
   *   the existing noteId (createOrReplaceNote is upsert by noteId).
   */
  mode?: AddNoteModalMode;
  note?: TaskNoteDto;
  onClose: () => void;
  onCreated?: (note: TaskNoteDto) => void;
  onError?: (error: unknown) => void;
}

const formatTimestamp = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

const titleFor = (mode: AddNoteModalMode): string => {
  if (mode === 'view') return 'Note';
  if (mode === 'edit') return 'Edit note';
  return 'Add note';
};

/**
 * AddNoteModal - create / view / edit dialog for task notes.
 *
 * Create flow preserves the PUT-by-noteId idempotency: a network failure
 * followed by a retry keeps the same client-generated note id until the
 * request succeeds. Edit flow reuses the existing note's id (the same
 * PUT endpoint is upsert), so editing is "PUT with a known id".
 */
export const AddNoteModal: React.FC<AddNoteModalProps> = ({
  taskId,
  tenantId,
  open,
  mode = 'create',
  note,
  onClose,
  onCreated,
  onError,
}) => {
  const canCreate = useCanCreateNotes();
  const canRead = useCanReadNotes();
  const { data: categories } = useNoteCategories(tenantId, false);

  const [currentMode, setCurrentMode] = useState<AddNoteModalMode>(mode);
  const [resetKey, setResetKey] = useState(0);
  const [body, setBody] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  // Clear stale error / confirm flag whenever the modal re-opens.
  useEffect(() => {
    if (open) {
      setSaveError(null);
      setShowSaveConfirm(false);
    }
  }, [open]);

  // Reset to caller's requested mode whenever the modal re-opens, so
  // the previous transition (e.g. view → edit) doesn't leak across opens.
  useEffect(() => {
    if (open) setCurrentMode(mode);
  }, [open, mode]);

  // Stable note id across retries for create mode. Regenerates after a
  // successful save (resetKey bump) or on re-open.
  const createNoteId = useMemo(
    () => uuidv4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resetKey]
  );
  const noteId =
    (currentMode === 'edit' || currentMode === 'view') && note
      ? note.noteId
      : createNoteId;

  const nonRetired = useMemo<NoteCategoryDto[]>(
    () => (categories ?? []).filter((c) => !c.isRetired),
    [categories]
  );

  const defaultCategoryId = useMemo(() => {
    const general = nonRetired.find((c) => c.name === 'General');
    if (general) return general.categoryId;
    const defaultGeneralId = DEFAULT_NOTE_CATEGORIES.General;
    if (nonRetired.some((c) => c.categoryId === defaultGeneralId)) {
      return defaultGeneralId;
    }
    return nonRetired[0]?.categoryId ?? '';
  }, [nonRetired]);

  // Load the form from the source note on open / mode transition, or clear it
  // when the modal closes. Deliberately NOT keyed on `categoryId` or `body`:
  // in edit mode the Category select and body field are user-editable, and
  // re-running this on every selection/keystroke would revert the change and
  // wipe in-progress body edits as collateral.
  useEffect(() => {
    if (!open) {
      setBody('');
      setCategoryId('');
      return;
    }
    if (currentMode === 'view' || currentMode === 'edit') {
      if (note) {
        setBody(note.body ?? '');
        setCategoryId(note.categoryId ?? '');
      }
    }
  }, [open, currentMode, note]);

  // Create mode only: seed the default category once it resolves and the user
  // hasn't chosen one yet. Safe to re-run on `categoryId` changes — the guard
  // makes it a no-op after the first selection.
  useEffect(() => {
    if (!open || currentMode !== 'create') return;
    if (!categoryId && defaultCategoryId) {
      setCategoryId(defaultCategoryId);
    }
  }, [open, currentMode, defaultCategoryId, categoryId]);

  // Permission gates per mode. View needs read:notes; edit/create need
  // manage:notes. The NotesPanel already filters out the tab entirely
  // when the user has neither permission, but defend the modal too.
  const allowed = currentMode === 'view' ? canRead : canCreate;
  if (!allowed) return null;

  const performSave = async () => {
    if (!taskId || !categoryId || isSaving) return;
    if (currentMode === 'view') return;
    setSaveError(null);
    setIsSaving(true);
    try {
      const saved = await createOrReplaceNote(taskId, noteId, {
        categoryId,
        body: body.trim() || undefined,
      });
      // Create re-rolls the noteId for the next opening.
      if (currentMode === 'create') {
        setResetKey((k) => k + 1);
      }
      onCreated?.(saved);
      // Both create and edit fully dismiss the modal on success — the user
      // returns to the underlying Notes tab where the grid has refreshed.
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('AddNoteModal save failed', e);
      setSaveError(
        e instanceof Error && e.message
          ? e.message
          : 'Save failed. Please try again.'
      );
      onError?.(e);
    } finally {
      setIsSaving(false);
    }
  };

  // Edit mode requires explicit confirmation before persisting. Create
  // saves directly: the user has already chosen to add a brand-new note.
  const handleSaveClick = () => {
    if (currentMode === 'edit') {
      setShowSaveConfirm(true);
      return;
    }
    performSave();
  };

  const isEmpty = nonRetired.length === 0;
  const isReadOnly = currentMode === 'view';
  const showEditButton = currentMode === 'view' && canCreate && !!note;
  const trimmedBody = body.trim();
  const isBlank = trimmedBody === '';
  const noEditChanges =
    currentMode === 'edit' && note
      ? trimmedBody === (note.body?.trim() ?? '') &&
        // Normalize: the empty selection is stored as '' while a note with no
        // category has categoryId === undefined, so without the `?? ''` an
        // unchanged uncategorized note would compare '' !== undefined and leave
        // Save enabled for a no-op PUT.
        categoryId === (note.categoryId ?? '')
      : false;
  const saveDisabled =
    !categoryId || isSaving || isEmpty || isBlank || noEditChanges;

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      data-testid="add-note-modal"
      data-mode={currentMode}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{titleFor(currentMode)}</DialogTitle>
      <DialogContent>
        {saveError && (
          <Alert
            severity="error"
            sx={{ marginBottom: 2 }}
            data-testid="add-note-error"
          >
            {saveError}
          </Alert>
        )}
        {currentMode === 'view' && note ? (
          <Stack spacing={1.5} sx={{ paddingTop: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Category
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
                data-testid="view-note-category"
              >
                {note.categoryName ?? '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Author
              </Typography>
              <Typography
                variant="body2"
                data-testid="view-note-author"
                sx={{ overflowWrap: 'anywhere' }}
              >
                {note.authoredBy || note.authoredByUserId || 'Unknown'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body2" data-testid="view-note-date">
                {formatTimestamp(note.authoredAtUtc)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Note
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
                data-testid="view-note-body"
              >
                {note.body ?? ''}
              </Typography>
            </Box>
          </Stack>
        ) : isEmpty ? (
          <Box sx={{ paddingY: 1 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="add-note-empty-categories-hint"
            >
              Contact your administrator to add a note category before creating
              notes.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2} sx={{ paddingTop: 1 }}>
            <TextField
              select
              size="small"
              label="Category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              data-testid="add-note-category"
              inputProps={{ 'data-testid': 'add-note-category-input' }}
              disabled={isReadOnly || isSaving}
            >
              {nonRetired.map((c) => (
                <MenuItem key={c.categoryId} value={c.categoryId}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Note"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              multiline
              minRows={3}
              maxRows={8}
              data-testid="add-note-body"
              inputProps={{ 'data-testid': 'add-note-body-input' }}
              disabled={isReadOnly || isSaving}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {currentMode === 'view' ? (
          <>
            <Button onClick={onClose} data-testid="view-note-close">
              Close
            </Button>
            {showEditButton && (
              <Button
                variant="contained"
                onClick={() => setCurrentMode('edit')}
                data-testid="view-note-edit"
              >
                Edit
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                if (currentMode === 'edit' && note) {
                  // Return to view; restore values from the original note.
                  setBody(note.body ?? '');
                  setCategoryId(note.categoryId ?? '');
                  setCurrentMode('view');
                } else {
                  onClose();
                }
              }}
              disabled={isSaving}
              data-testid="add-note-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveClick}
              disabled={saveDisabled}
              data-testid="add-note-save"
            >
              {isSaving ? <CircularProgress size={16} /> : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
      {showSaveConfirm && (
        <ConfirmModal
          confirmTitle="Save changes?"
          confirmContent="Save your changes to this note?"
          confirmButtonLabel="Save"
          denyButtonLabel="Cancel"
          onConfirm={() => {
            setShowSaveConfirm(false);
            performSave();
          }}
          onDeny={() => setShowSaveConfirm(false)}
        />
      )}
    </Dialog>
  );
};
